import AgentDocumentationIcon from './svg/agentDocumentationIcon.svg?react';
import AgentProcessIcon from './svg/agentProcessIcon.svg?react';
import AgentMonitoringIcon from './svg/agentMonitoringIcon.svg?react';
import ChatDocumentationIcon from './svg/chatDocumentationIcon.svg?react';
import ChatProcessIcon from './svg/chatProcessIcon.svg?react';
import ChatMonitoringIcon from './svg/chatMonitoringIcon.svg?react';
import ChatPermissionsIcon from './svg/chatPermissionsIcon.svg?react';
import AgentPermissionsIcon from './svg/agentPermissionsIcon.svg?react';
import type {
  AgentFileProperties,
  AiAgentBestPracticeAttachment,
  AiAgentProperties,
  AiAgentPropertiesWithFacts,
} from '@server/ai';
import { AgentType } from '@server/ai';
import type { ReactNode } from 'react';
import NewProcessAgentIcon from './svg/newProcessAgentIcon.svg?react';
import NewMonitoringAgentIcon from './svg/newMonitoringAgentIcon.svg?react';
import type { Issue } from '../issues/issuesTypes';
import type { ActiveScan, IssueStatus } from '@server/issues';
import { colors } from '@sweep-io/sweep-design';
import {
  DOCUMENTATION_PAGEBLOCK_TEXT,
  MONITORING_PAGEBLOCK_TEXT,
  PROCESS_PAGEBLOCK_TEXT,
} from '../../constants/pageblockData';

export const CHAT_AREA_MAX_BY_DESIGN = 924;
export const CHAT_AREA_SIDE_PADDING = 12;
export const CHAT_AREA_MAX_WIDTH = CHAT_AREA_MAX_BY_DESIGN + 2 * CHAT_AREA_SIDE_PADDING + 'px';

export const ARTIFACT_PREVIEW_NARROW_QUERY = '@container (max-width: 459px)';
export const ISSUES_CARD_NARROW_QUERY = '@container (max-width: 520px)';

export interface AgentFact {
  icon: ReactNode;
  text: string;
}

/**
 * BE is enumerating AgentType therefore it cannot be extended with fake types directly
 */
export enum FakeAgentType {
  SupportAgent = 'SupportAgent',
}

export type AgentTypeWithFake = AgentType | FakeAgentType;

export enum FakeAgentTypeKey {
  OPEN_CONVERSATIONS = 'openConversations',
  RESOLVED_CONVERSATIONS = 'resolvedConversations',
}

export interface UserSupportAgentFact extends AgentFact {
  key: FakeAgentTypeKey;
  value: number;
}

export interface AgentFile extends Omit<AgentFileProperties, 'createdAt'> {
  createdAt: string; //in the BE it is "Date" so can't reuse the type
}

export interface AiAgentWithFacts extends Omit<
  AiAgentPropertiesWithFacts,
  'updatedAt' | 'createdAt' | 'files' | 'bestPractices'
> {
  updatedAt: string; //in the BE it is "Date" so can't reuse the type
  files: AgentFile[];
  bestPractices: (Omit<AiAgentBestPracticeAttachment, 'createdAt'> & { createdAt: string })[];
  isLoadingIssues?: boolean;
  issues?: Issue[]; //Currently only relevant for AgentType.MonitoringV2
  activeScan?: ActiveScan; //Currently only relevant for AgentType.MonitoringV2
}

export interface AiAgent extends Omit<
  AiAgentProperties,
  'updatedAt' | 'createdAt' | 'files' | 'bestPractices'
> {
  updatedAt: string; //in the BE it is "Date" so can't reuse the type
  files: AgentFile[];
  bestPractices: (Omit<AiAgentBestPracticeAttachment, 'createdAt'> & { createdAt: string })[];
  isLoadingIssues?: boolean;
  issues?: Issue[]; //Currently only relevant for AgentType.MonitoringV2
  activeScan?: ActiveScan; //Currently only relevant for AgentType.MonitoringV2
}

const PROCESS_AGENT_PROMPTS = [
  'Highlight process optimization opportunities',
  'Describe the process playbook',
];

const MONITORING_AGENT_PROMPTS = [
  'Show urgent risks for this object',
  'Generate a technical quality score',
  'Suggest the top optimization for this object',
];

export enum PromptsLocation {
  WHOLE_CHAT_AREA = 'WHOLE_CHAT_AREA',
  BELOW_CONTEXT = 'BELOW_CONTEXT',
}

export type BaseAgentProps = {
  color: string;
  isReplacedBy?: AgentType; // if there's a newer version for this type, e.g. Monitoring V2
  name: string;
  description: string;
  agentIcon: ReactNode;
  instructionsPlaceholder?: string;
  addingFiles?: {
    isAllowed: boolean;
  };
  createEntryPoint: {
    isAllowed: boolean;
    nameInCard?: string;
    nameInMenu?: string;
    description?: string;
    icon?: ReactNode;
  };
  chatIcon: ReactNode;
  introMessage_deprecated?: {
    text: string;
  };
  prompts?: {
    timing_deprecated: PromptsLocation;
    texts: string[];
  };
  paywallNumberOfAllowedUserMessages_deprecated: number;
  paywallNumberOfAllowedAgents_deprecated?: number; //undefined = no limit
  isSupportBestPractices: boolean;
  isSupportIssues: boolean;
  isSupportArtifacts: boolean;
  notSubscribedText: string;
};

export type OnUpdateIssueStatus = ({
  resourceName,
  issueId,
  status,
}: {
  resourceName: string;
  issueId: string;
  status: IssueStatus;
}) => Promise<void>;

type SupportAgentProps = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

type MonitoringAgentProps = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

type MonitoringAgentV2Props = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

type ProcessAgentProps = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

type DocumentationAgentProps = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

type PermissionsAgentProps = BaseAgentProps & {
  RenderContext_deprecated?: undefined;
};

const DOCUMENTATION_FIRST_MSG = `Document your Salesforce configuration, automatically. Ask anything about your metadata to understand how your org is built and connected.`;
const PERMISSIONS_FIRST_MSG = `Ask anything about permissions defined in your Salesforce configuration.`;

export const AI_AGENTS_INFO: {
  [AgentType.Documentation]: DocumentationAgentProps;
  [AgentType.Monitoring]: MonitoringAgentProps;
  [AgentType.ProcessOptimization]: ProcessAgentProps;
  [AgentType.MonitoringV2]: MonitoringAgentV2Props;
  [AgentType.Permissions]: PermissionsAgentProps;
  [FakeAgentType.SupportAgent]: SupportAgentProps;
} = {
  [AgentType.Documentation]: {
    color: colors.sun[100],
    name: 'Documentation',
    description: 'Generates real-time documentation of Salesforce configurations and dependencies',
    agentIcon: <AgentDocumentationIcon />,
    createEntryPoint: {
      isAllowed: false,
    },
    chatIcon: <ChatDocumentationIcon />,
    introMessage_deprecated: {
      text: DOCUMENTATION_FIRST_MSG,
    },
    paywallNumberOfAllowedUserMessages_deprecated: 1,
    isSupportBestPractices: false,
    isSupportIssues: false,
    isSupportArtifacts: true,
    notSubscribedText: MONITORING_PAGEBLOCK_TEXT,
  },
  [AgentType.ProcessOptimization]: {
    color: colors.lilac[100],
    name: 'Process',
    description: 'Identify process bottlenecks and receive actionable recommendations',
    agentIcon: <AgentProcessIcon />,
    addingFiles: {
      isAllowed: true,
    },
    instructionsPlaceholder: `e.g., "Focus on conversion of MQL --> Demo Booked. Keep answers short an focused. Elaborate on the issues your recommendations address."`,
    createEntryPoint: {
      isAllowed: true,
      nameInCard: 'Create a new process agent',
      nameInMenu: 'Process',
      description: 'Detect bottlenecks and opportunities in your process',
      icon: <NewProcessAgentIcon />,
    },
    chatIcon: <ChatProcessIcon />,
    prompts: {
      texts: PROCESS_AGENT_PROMPTS,
      timing_deprecated: PromptsLocation.WHOLE_CHAT_AREA,
    },
    paywallNumberOfAllowedUserMessages_deprecated: 2,
    isSupportBestPractices: false,
    isSupportIssues: false,
    isSupportArtifacts: false,
    notSubscribedText: PROCESS_PAGEBLOCK_TEXT,
  },
  [AgentType.Monitoring]: {
    // Disabled placeholder - functionality moved to MonitoringV2 - can delete when "Monitoring" is removed from AgentType
    color: colors.seafoam[100],
    name: 'Monitoring (Legacy)',
    description: 'Legacy monitoring agent - use MonitoringV2 instead',
    agentIcon: <AgentMonitoringIcon />,
    createEntryPoint: {
      isAllowed: false, // Disabled - cannot create new instances
    },
    chatIcon: <ChatMonitoringIcon />,
    paywallNumberOfAllowedUserMessages_deprecated: 0,
    isSupportBestPractices: false,
    isSupportIssues: false,
    isSupportArtifacts: false,
    notSubscribedText: '', // not applicable - deprecated type
  },
  [AgentType.MonitoringV2]: {
    color: colors.seafoam[100],
    name: 'Monitoring',
    description: 'Identifies potential issues and risks in your Salesforce configuration',
    agentIcon: <AgentMonitoringIcon />,
    instructionsPlaceholder: `e.g., "Focus on issues in Apex code. Keep answers short and focused. Highlight key violations and recommended fixes."`,
    addingFiles: {
      isAllowed: true,
    },
    createEntryPoint: {
      isAllowed: true,
      nameInCard: 'Create a new monitoring agent',
      nameInMenu: 'Monitoring',
      description: 'Identify potential issues in your Salesforce configuration',
      icon: <NewMonitoringAgentIcon />,
    },
    chatIcon: <ChatMonitoringIcon />,
    prompts: {
      texts: MONITORING_AGENT_PROMPTS,
      timing_deprecated: PromptsLocation.BELOW_CONTEXT,
    },
    paywallNumberOfAllowedUserMessages_deprecated: 1,
    paywallNumberOfAllowedAgents_deprecated: 1,
    isSupportBestPractices: true,
    isSupportIssues: true,
    isSupportArtifacts: false,
    notSubscribedText: DOCUMENTATION_PAGEBLOCK_TEXT,
  },
  [AgentType.Permissions]: {
    color: colors.sky[100],
    name: 'Permissions',
    description: 'Analyze Salesforce profiles, permission sets, and user access',
    agentIcon: <AgentPermissionsIcon />,
    createEntryPoint: {
      isAllowed: false,
    },
    chatIcon: <ChatPermissionsIcon />,
    introMessage_deprecated: {
      text: PERMISSIONS_FIRST_MSG,
    },
    paywallNumberOfAllowedUserMessages_deprecated: 1,
    isSupportBestPractices: false,
    isSupportIssues: false,
    isSupportArtifacts: false,
    notSubscribedText: '', //TODO need to add before opening the FT
  },

  [FakeAgentType.SupportAgent]: {
    color: colors.blush[100],
    name: 'User Support Agent',
    description: 'Answers end-user questions in Salesforce and frees admins for higher value work.',
    agentIcon: <AgentMonitoringIcon />,
    createEntryPoint: {
      isAllowed: false, // Disabled - cannot create new instances
    },
    chatIcon: <></>,
    paywallNumberOfAllowedUserMessages_deprecated: 0,
    isSupportBestPractices: false,
    isSupportIssues: false,
    isSupportArtifacts: false,
    notSubscribedText: '', //not relevant in this case
  },
};

export const SUPPORTED_TYPES_FOR_CREATION = Object.entries(AI_AGENTS_INFO)
  .filter(([, props]) => props.createEntryPoint.isAllowed)
  .map(([type]) => type as AgentType);

export const TOP_BAR_COMMON_SX = {
  display: 'flex',
  alignItems: 'center',
  height: '56px',
  padding: '12px',
  gap: '8px',
};

export type AgentFileInvalid = {
  id: string;
  name: string;
  message: string;
};

export const INSTRUCTIONS_SUBTITLE = `Add instruction to your AI agent to give it more context about your configuration, ask it to focus on certain topics, or to set tone or format for responses.`;
